import { useEffect, useState } from 'react'
import { RadialBarChart, RadialBar, ResponsiveContainer, PolarAngleAxis } from 'recharts'
import {
  DownloadSimple, Buildings, Lightning, Drop,
  Factory, ShieldWarning, Leaf, Warning, FileCsv
} from '@phosphor-icons/react'
import { supabase } from '../../lib/supabase'
import { downloadCSV, openPrintWindow } from '../esg/exportHelpers'

const ACCENT = {
  coral:    '#D4614A',
  slate:    '#7A8FA0',
  olive:    '#8D9A2E',
  amber:    '#C98A2E',
  charcoal: '#4A4A4A',
}

const CARD = {
  background: '#FFFFFF',
  borderRadius: '8px',
  padding: '1.5rem',
  boxShadow: '0 1px 4px rgba(59,47,47,0.07)',
  border: '1px solid #DDD5C8',
}

const RISK_CONFIG = {
  alto:  { label: 'Alto',  color: ACCENT.coral },
  medio: { label: 'Medio', color: ACCENT.amber },
  bajo:  { label: 'Bajo',  color: '#B0A89E' },
}

const ESG_RATING_CONFIG = {
  A: ACCENT.olive,
  B: ACCENT.amber,
  C: '#B0A89E',
  D: '#B0A89E',
  E: ACCENT.coral,
}

function MiniGauge({ pct, color }) {
  const data = [{ value: pct, fill: color }]
  return (
    <ResponsiveContainer width={100} height={65}>
      <RadialBarChart cx="50%" cy="75%" innerRadius="65%" outerRadius="100%" startAngle={180} endAngle={0} data={data} barSize={10}>
        <PolarAngleAxis type="number" domain={[0, 100]} angleAxisId={0} tick={false} />
        <RadialBar background={{ fill: '#EDE7DE' }} dataKey="value" cornerRadius={5} angleAxisId={0} />
      </RadialBarChart>
    </ResponsiveContainer>
  )
}

function parseJ(ev) {
  if (!ev?.justificacion) return null
  try { return JSON.parse(ev.justificacion) } catch { return null }
}

export default function PanelInversor({ project, BREEAM_CRITERIA }) {
  const [surface, setSurface] = useState(project?.surface_m2 || 0)
  const [year, setYear] = useState(2023)
  const [address, setAddress] = useState(project?.municipality || '')
  const [linked, setLinked] = useState({ capex: null, circular: null, gresb: null, sensors: null, iros: [], iroSettings: null })

  // Sincronizar superficie con project.surface_m2 cuando cambie el proyecto
  useEffect(() => {
    if (project?.surface_m2) setSurface(project.surface_m2)
    if (project?.municipality) setAddress(project.municipality)
  }, [project?.surface_m2, project?.municipality])

  // Cargar últimos snapshots de los otros módulos para alimentar KPIs reales
  useEffect(() => {
    let cancel = false
    async function load() {
      if (!project?.id) return
      const [capex, circular, gresb, sensors, iros, iroSet] = await Promise.all([
        supabase.from('esg_snapshots').select('source_data, value_numeric, created_at').eq('project_id', project.id).eq('framework', 'capex_opex').order('created_at', { ascending: false }).limit(1),
        supabase.from('esg_snapshots').select('value_numeric, created_at').eq('project_id', project.id).eq('framework', 'circular').eq('indicator_code', 'DGNB_CI').order('created_at', { ascending: false }).limit(1),
        supabase.from('esg_snapshots').select('value_numeric, source_data, created_at').eq('project_id', project.id).eq('framework', 'gresb').order('created_at', { ascending: false }).limit(1),
        supabase.from('building_sensors').select('*').eq('project_id', project.id).order('reading_year', { ascending: false }),
        supabase.from('operational_iros').select('*').eq('project_id', project.id).neq('status', 'dismissed').neq('status', 'closed'),
        supabase.from('operational_iro_settings').select('*').eq('project_id', project.id).maybeSingle(),
      ])
      if (cancel) return
      let sensorAgg = null
      if (sensors.data?.length) {
        const yMap = new Map()
        sensors.data.forEach(r => {
          const a = yMap.get(r.reading_year) || { year: r.reading_year, kwh: 0, m3: 0, scope1: 0, scope2: 0, scope3: 0 }
          a.kwh += +(r.energy_kwh_total || 0); a.m3 += +(r.water_m3_total || 0)
          a.scope1 += +(r.scope1_kgco2e || 0); a.scope2 += +(r.scope2_kgco2e || 0); a.scope3 += +(r.scope3_kgco2e || 0)
          yMap.set(r.reading_year, a)
        })
        const years = [...yMap.keys()].sort((a, b) => b - a)
        sensorAgg = yMap.get(years[0])
      }
      setLinked({
        capex: capex.data?.[0] || null,
        circular: circular.data?.[0]?.value_numeric ?? null,
        gresb: gresb.data?.[0] || null,
        sensors: sensorAgg,
        iros: iros.data || [],
        iroSettings: iroSet.data || null,
      })
    }
    load()
    return () => { cancel = true }
  }, [project?.id])

  const totalPoints = project?.totalPoints || 0
  const maxPoints = project?.maxPoints || 110

  const evaluations = project?.evaluations ? Object.values(project.evaluations) : []

  // Conjunto de claves válidas del esquema BREEAM (excluye auxiliares TRA-DATA, etc)
  const validCriteriaKeys = new Set()
  const BREEAM_WEIGHTS = {
    'Gestión': 0.115, 'Salud y Bienestar': 0.14, 'Energía': 0.18,
    'Transporte': 0.08, 'Agua': 0.105, 'Materiales': 0.12,
    'Residuos': 0.07, 'Uso del Suelo y Ecología': 0.095, 'Contaminación': 0.095,
  }
  Object.entries(BREEAM_CRITERIA || {}).forEach(([cat, data]) => {
    Object.keys(data.criteria).forEach(code => validCriteriaKeys.add(`${cat}-${code}`))
  })
  const totalCriteria = validCriteriaKeys.size
  const validEvaluations = evaluations.filter(e => validCriteriaKeys.has(`${e.category}-${e.criteria_code}`))
  const evidencesPct = totalCriteria > 0 ? Math.min(100, Math.round((validEvaluations.length / totalCriteria) * 100)) : 0

  // Puntuación BREEAM ponderada Tabla 5 + Innovación como bonus directo
  const evalsMapForScore = project?.evaluations || {}
  const breeamPctExact = (() => {
    let weightedTotal = 0
    Object.entries(BREEAM_CRITERIA || {}).forEach(([cat, data]) => {
      if (cat === 'Innovación') {
        const pts = Object.keys(data.criteria).reduce((s, code) => {
          const ev = evalsMapForScore[`${cat}-${code}`]
          return s + ((ev?.puntos_auditoria ?? ev?.points_obtained) || 0)
        }, 0)
        weightedTotal += pts
        return
      }
      let obt = 0, max = 0
      Object.entries(data.criteria).forEach(([code, c]) => {
        const ev = evalsMapForScore[`${cat}-${code}`]
        let isNA = false
        try { isNA = !!JSON.parse(ev?.justificacion || 'null')?.no_aplica } catch {}
        if (isNA) return
        max += (ev?.points_max ?? c.points ?? 0)
        obt += (ev?.puntos_auditoria ?? ev?.points_obtained) || 0
      })
      if (max > 0) weightedTotal += (obt / max) * (BREEAM_WEIGHTS[cat] || 0) * 100
    })
    return Math.round(weightedTotal * 10) / 10
  })()
  const breeamPct = Math.round(breeamPctExact)
  const esgRating = breeamPctExact >= 70 ? 'A' : breeamPctExact >= 55 ? 'B' : breeamPctExact >= 45 ? 'C' : breeamPctExact >= 30 ? 'D' : 'E'

  const energiaPoints = evaluations.filter(e => e.category === 'Energía').reduce((s, e) => s + ((e.puntos_auditoria ?? e.points_obtained) || 0), 0)

  // ── Datos reales de las calculadoras BREEAM (no proxies) ──
  const evalsMap = project?.evaluations || {}
  const ene1J = parseJ(evalsMap['Energía-ENE 1'])
  const ag1J = parseJ(evalsMap['Agua-AG 1'])
  // ENE 1: intensidad CO2 y kWh/m²·año (ponderadas por zona)
  const ene1Intensity = (() => {
    const z = ene1J?.ene1_calculadora?.zonas?.filter(x => parseFloat(x.m2) > 0)
    if (!z?.length) return { co2: null, kwh: null, m2: null }
    const m2Total = z.reduce((s, x) => s + parseFloat(x.m2), 0)
    const co2 = z.reduce((s, x) => s + parseFloat(x.co2_eval || 0) * parseFloat(x.m2), 0) / m2Total
    const kwh = z.reduce((s, x) => s + parseFloat(x.con_eval || 0) * parseFloat(x.m2), 0) / m2Total
    return { co2, kwh, m2: m2Total }
  })()
  // AG 1: nivel global de eficiencia hídrica (0-5)
  const ag1Level = ag1J?.ag1_calculadora?.nivelGlobal ?? null

  // ── Alineación Taxonomía UE: % objetivos que cumplen umbral BREEAM ──
  const taxonomyAlignment = (() => {
    const objectiveCats = [
      { obj: 1, cats: ['Energía'] },
      { obj: 2, cats: ['Salud y Bienestar'] },
      { obj: 3, cats: ['Agua'] },
      { obj: 4, cats: ['Materiales','Residuos'] },
      { obj: 5, cats: ['Contaminación'] },
      { obj: 6, cats: ['Uso del Suelo y Ecología'] },
    ]
    let aligned = 0
    objectiveCats.forEach(({ cats }) => {
      let obt = 0, max = 0
      cats.forEach(cat => {
        const catData = BREEAM_CRITERIA?.[cat]
        if (!catData) return
        Object.entries(catData.criteria).forEach(([code, c]) => {
          const ev = evalsMap[`${cat}-${code}`]
          max += (ev?.points_max ?? c.points ?? 0)
          obt += (ev?.puntos_auditoria ?? ev?.points_obtained) || 0
        })
      })
      if (max > 0 && (obt / max) >= 0.55) aligned++  // Umbral contribución sustancial
    })
    return Math.round((aligned / objectiveCats.length) * 100)
  })()

  // ── Riesgo climático físico: leído desde IROs D10/D11 + ARPSI ──
  const climateRisk = (() => {
    const d10 = linked.iros?.find(i => i.detector_id === 'D10.calor_cronico')
    const d11 = linked.iros?.find(i => i.detector_id === 'D11.riesgo_inundacion')
    const arpsi = linked.iroSettings?.arpsi_status
    const activos = []
    if (d10) activos.push({ sev: d10.severity, tipo: 'Calor crónico' })
    if (d11) activos.push({ sev: d11.severity, tipo: 'Inundación (ARPSI)' })
    const maxSev = activos.reduce((s, r) => Math.max(s, r.sev || 0), 0)
    let level = 'no_evaluado'
    if (activos.length === 0) {
      if (arpsi === 'not_at_risk') level = 'bajo'
      else if (arpsi === 'not_checked' || !arpsi) level = 'no_evaluado'
      else level = 'bajo'
    } else if (maxSev >= 4) level = 'alto'
    else if (maxSev >= 3) level = 'medio'
    else level = 'bajo'
    return { level, activos, arpsi }
  })()

  // Nº de riesgos regulatorios: IROs de tipo 'risk' abiertos, severidad ≥ 3
  const regulatoryRisksCount = (linked.iros || []).filter(i => i.type === 'risk' && (i.severity || 0) >= 3).length

  // OPEX 10y: solo si hay snapshot CapexOpex con ahorro anual real.
  const annualSavingsFromCapex = (linked.capex?.source_data?.energySaving || 0) + (linked.capex?.source_data?.waterSaving || 0)
  const opexSaving10y = annualSavingsFromCapex > 0 ? annualSavingsFromCapex * 10 : null
  const capexInvested = linked.capex?.value_numeric ?? null
  const gresbPts = linked.gresb?.value_numeric ?? null
  const totalEmissionsT = linked.sensors ? (linked.sensors.scope1 + linked.sensors.scope2 + linked.sensors.scope3) / 1000 : null

  const getClassification = (pct) => {
    if (pct >= 85) return 'Excepcional'
    if (pct >= 70) return 'Excelente'
    if (pct >= 55) return 'Muy Bueno'
    if (pct >= 45) return 'Bueno'
    if (pct >= 30) return 'Correcto'
    return 'Aprobado'
  }

  const handleExportPDF = () => openPrintWindow({
    title: 'Panel del Inversor — Síntesis ESG',
    projectName: project?.name || 'Proyecto',
    sections: [
      { title: 'Resumen del activo', cols: ['KPI', 'Valor'], rows: [
        ['Superficie',                 `${surface.toLocaleString()} m²`],
        ['Año construcción',           year],
        ['Dirección',                  address || '—'],
        ['BREEAM Score',               `${breeamPctExact.toFixed(1)}% (${getClassification(breeamPctExact)})`],
        ['Rating ESG',                 esgRating],
        ['Cobertura expediente',       `${evidencesPct}%`],
        ['Ahorro OpEx 10 años',        opexSaving10y != null ? `${opexSaving10y.toLocaleString()} €` : 'Pendiente — cargar en Capex/OpEx'],
        ['Inversión verde (CapEx)',    capexInvested != null ? `${capexInvested.toLocaleString()} €` : 'sin snapshot'],
        ['CI (Econ Circular DGNB)',    linked.circular != null ? linked.circular.toFixed(2) : '—'],
        ['GRESB pts estimados',        gresbPts != null ? gresbPts.toFixed(1) : '—'],
        ['Emisiones totales (S1+S2+S3)', totalEmissionsT != null ? `${totalEmissionsT.toFixed(1)} tCO₂e/año` : '—'],
      ]},
      { title: 'Riesgos ESG', cols: ['Categoría', 'Nivel', 'Mitigación'],
        rows: dynamicRisks.map(r => [r.category, RISK_CONFIG[r.level].label, r.mitigation]) },
    ],
  })

  const handleExportCSV = () => downloadCSV([
    { kpi: 'BREEAM Score',              valor: `${breeamPctExact.toFixed(1)}%` },
    { kpi: 'Clasificación BREEAM',      valor: getClassification(breeamPctExact) },
    { kpi: 'Rating ESG',                valor: esgRating },
    { kpi: 'Cobertura expediente',      valor: `${evidencesPct}%` },
    { kpi: 'Ahorro OpEx 10y (€)',       valor: opexSaving10y ?? '' },
    { kpi: 'CapEx invertido (€)',       valor: capexInvested ?? '' },
    { kpi: 'CI DGNB',                   valor: linked.circular ?? '' },
    { kpi: 'GRESB pts',                 valor: gresbPts ?? '' },
    { kpi: 'Emisiones S1+S2+S3 (tCO2e)', valor: totalEmissionsT != null ? totalEmissionsT.toFixed(2) : '' },
  ], `PanelInversor_${(project?.name || 'proyecto').replace(/[^\w-]+/g, '_')}_${new Date().toISOString().slice(0, 10)}.csv`)

  // Semáforo de Riesgos dinámico basado en datos reales
  const dynamicRisks = (() => {
    const risks = []
    const d7 = linked.iros?.find(i => i.detector_id === 'D7.eu_ets')
    const d8 = linked.iros?.find(i => i.detector_id === 'D8.riesgo_zbe')
    if (d7 || d8) {
      const sev = Math.max(d7?.severity || 0, d8?.severity || 0)
      risks.push({
        category: 'Regulatorio (transición)',
        level: sev >= 4 ? 'alto' : sev >= 3 ? 'medio' : 'bajo',
        mitigation: [d7 && `EU ETS: exposición sev ${d7.severity}/5`, d8 && `ZBE ${linked.iroSettings?.municipality || 'municipio'}: sev ${d8.severity}/5`].filter(Boolean).join(' · '),
      })
    } else {
      risks.push({
        category: 'Regulatorio',
        level: evaluations.some(e => e.puntos_auditoria != null) ? 'bajo' : 'medio',
        mitigation: evaluations.some(e => e.puntos_auditoria != null)
          ? 'Certificado BREEAM oficial emitido · Taxonomía UE seguida'
          : 'Expediente BREEAM en curso · CSRD/Taxonomía UE aplicable desde 2025',
      })
    }
    const eneMax = 15
    const enePct = (energiaPoints / eneMax) * 100
    risks.push({
      category: 'Transición energética',
      level: enePct >= 60 ? 'bajo' : enePct >= 30 ? 'medio' : 'alto',
      mitigation: enePct >= 60
        ? `ENE 1: ${energiaPoints}/${eneMax} pts (${Math.round(enePct)}%) — edificio eficiente`
        : `ENE 1: ${energiaPoints}/${eneMax} pts — plan de mejora recomendado`,
    })
    risks.push({
      category: 'Físico climático',
      level: climateRisk.level === 'no_evaluado' ? 'medio' : climateRisk.level,
      mitigation: climateRisk.activos.length
        ? `${climateRisk.activos.map(a => `${a.tipo} sev ${a.sev}/5`).join(' · ')}`
        : climateRisk.arpsi === 'not_at_risk'
          ? 'ARPSI MITECO verificado · sin riesgo de inundación'
          : 'Sin evaluación de riesgos físicos · ejecutar módulo Monitorización',
    })
    const marketLevel = breeamPct >= 55 ? 'bajo' : breeamPct >= 30 ? 'medio' : 'alto'
    risks.push({
      category: 'Mercado',
      level: marketLevel,
      mitigation: `Clasificación ${getClassification(breeamPct)}${breeamPct >= 55 ? ' · activo diferenciable en mercado ESG' : ' · limitada diferenciación de mercado'}`,
    })
    const audited = evaluations.some(e => e.puntos_auditoria != null)
    risks.push({
      category: 'Reputacional',
      level: audited ? 'bajo' : evidencesPct >= 60 ? 'medio' : 'alto',
      mitigation: audited
        ? 'Informe BREEAM oficial · comunicación ESG con dato certificado'
        : `Expediente ${evidencesPct}% completado · reforzar comunicación ESG`,
    })
    return risks
  })()

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem', fontFamily: "'DM Sans', sans-serif" }}>

      {/* Ficha del activo */}
      <div style={{ background: '#3B2F2F', borderRadius: '8px', overflow: 'hidden' }}>
        <div style={{ padding: '1.5rem 2rem', borderBottom: '1px solid rgba(245,240,234,0.1)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' }}>
            <div>
              <div style={{ fontSize: '0.65rem', letterSpacing: '0.08em', textTransform: 'uppercase', color: '#B0A89E', marginBottom: '0.3rem', fontWeight: '500' }}>Panel del Inversor · Síntesis Ejecutiva</div>
              <h2 style={{ margin: 0, fontSize: '1.4rem', fontWeight: '700', color: '#F5F0EA', letterSpacing: '-0.01em' }}>{project?.name || 'Activo sin nombre'}</h2>
              {address && <div style={{ fontSize: '0.8rem', color: '#B0A89E', marginTop: '0.25rem' }}>{address}</div>}
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button onClick={handleExportPDF}
              style={{ background: '#F5F0EA', color: '#3B2F2F', border: 'none', borderRadius: '4px', padding: '0.5rem 0.875rem', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.8rem', fontFamily: "'DM Sans', sans-serif" }}>
                <DownloadSimple size={14} /> Exportar PDF
              </button>
              <button onClick={handleExportCSV}
              style={{ background: 'transparent', border: '1px solid #DDD5C8', color: '#F5F0EA', borderRadius: '4px', padding: '0.5rem 0.875rem', fontWeight: '500', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.8rem', fontFamily: "'DM Sans', sans-serif" }}>
                <FileCsv size={14} /> CSV
              </button>
            </div>
          </div>
        </div>
        <div style={{ padding: '1rem 2rem', display: 'flex', gap: '2rem', flexWrap: 'wrap', alignItems: 'center' }}>
          {[
            { label: 'Tipología', value: 'Edificio Terciario' },
            { label: 'Año', value: year, editable: true, setter: setYear, type: 'number', width: '70px' },
            { label: 'Superficie (m²)', value: surface, editable: true, setter: setSurface, type: 'number', width: '90px' },
            { label: 'Promotor', value: '—' },
          ].map((field, i) => (
            <div key={i}>
              <div style={{ fontSize: '0.65rem', color: '#B0A89E', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.2rem' }}>{field.label}</div>
              {field.editable ? (
                <input
                  type={field.type}
                  value={field.value}
                  onChange={e => field.setter(parseInt(e.target.value) || 0)}
                  style={{ background: 'transparent', border: 'none', borderBottom: '1px solid rgba(245,240,234,0.2)', color: '#F5F0EA', fontSize: '0.9rem', fontWeight: '600', fontFamily: "'IBM Plex Mono', monospace", width: field.width, outline: 'none', padding: '0 0 2px' }}
                />
              ) : (
                <div style={{ color: '#F5F0EA', fontSize: '0.9rem', fontWeight: '500' }}>{field.value}</div>
              )}
            </div>
          ))}
          <div>
            <div style={{ fontSize: '0.65rem', color: '#B0A89E', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.2rem' }}>Dirección</div>
            <input
              type="text"
              placeholder="Introduce la dirección..."
              value={address}
              onChange={e => setAddress(e.target.value)}
              style={{ background: 'transparent', border: 'none', borderBottom: '1px solid rgba(245,240,234,0.2)', color: '#F5F0EA', fontSize: '0.9rem', fontWeight: '500', fontFamily: "'DM Sans', sans-serif", width: '220px', outline: 'none', padding: '0 0 2px' }}
            />
          </div>
        </div>
      </div>

      {/* Scorecard */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '1rem' }}>
        {/* BREEAM Score */}
        <div style={{ ...CARD, textAlign: 'center', gridColumn: 'span 1' }}>
          <div style={{ fontSize: '0.65rem', fontWeight: '600', color: '#7A6A5A', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '0.5rem' }}>Puntuación BREEAM</div>
          <MiniGauge pct={breeamPct} color={ACCENT.olive} />
          <div style={{ fontSize: '1.75rem', fontWeight: '700', fontFamily: "'IBM Plex Mono', monospace", color: ACCENT.olive, lineHeight: 1, marginTop: '-0.25rem' }}>{breeamPctExact.toFixed(1)}%</div>
          <div style={{ fontSize: '0.75rem', color: '#7A6A5A', marginTop: '0.25rem' }}>{getClassification(breeamPctExact)}</div>
          <div style={{ background: '#EDE7DE', borderRadius: '3px', height: '5px', overflow: 'hidden', marginTop: '0.75rem' }}>
            <div style={{ background: ACCENT.olive, height: '100%', width: `${Math.min(100, breeamPct)}%`, borderRadius: '3px' }} />
          </div>
          <div style={{ fontSize: '0.6rem', color: '#B0A89E', marginTop: '0.4rem' }}>Ponderado Tabla 5 + Innovación</div>
        </div>

        {/* ESG Rating */}
        <div style={{ ...CARD, textAlign: 'center' }}>
          <div style={{ fontSize: '0.65rem', fontWeight: '600', color: '#7A6A5A', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '0.875rem' }}>Rating ESG</div>
          <div style={{ display: 'flex', gap: '0.3rem', justifyContent: 'center', marginBottom: '0.875rem' }}>
            {['A', 'B', 'C', 'D', 'E'].map(r => (
              <div key={r} style={{
                width: '32px', height: '32px', borderRadius: '4px',
                background: r === esgRating ? ESG_RATING_CONFIG[r] : '#EDE7DE',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '0.875rem', fontWeight: '700',
                color: r === esgRating ? '#FFFFFF' : '#B0A89E',
                fontFamily: "'IBM Plex Mono', monospace",
              }}>
                {r}
              </div>
            ))}
          </div>
          <div style={{ fontSize: '0.75rem', color: '#7A6A5A' }}>basado en BREEAM score</div>
        </div>

        {/* Taxonomía UE */}
        <div style={{ ...CARD, textAlign: 'center' }}>
          <div style={{ fontSize: '0.65rem', fontWeight: '600', color: '#7A6A5A', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '0.5rem' }}>Alineación Taxonomía UE</div>
          <MiniGauge pct={taxonomyAlignment} color={taxonomyAlignment >= 50 ? ACCENT.olive : ACCENT.amber} />
          <div style={{ fontSize: '1.75rem', fontWeight: '700', fontFamily: "'IBM Plex Mono', monospace", color: taxonomyAlignment >= 50 ? ACCENT.olive : ACCENT.amber, lineHeight: 1, marginTop: '-0.25rem' }}>{taxonomyAlignment}%</div>
          <div style={{ fontSize: '0.75rem', color: '#7A6A5A', marginTop: '0.25rem' }}>{Math.round(taxonomyAlignment * 6 / 100)} de 6 objetivos ≥ 55%</div>
        </div>

        {/* Evidencias */}
        <div style={{ ...CARD, textAlign: 'center' }}>
          <div style={{ fontSize: '0.65rem', fontWeight: '600', color: '#7A6A5A', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '0.5rem' }}>Completitud Expediente</div>
          <MiniGauge pct={evidencesPct} color={evidencesPct > 60 ? ACCENT.olive : ACCENT.amber} />
          <div style={{ fontSize: '1.75rem', fontWeight: '700', fontFamily: "'IBM Plex Mono', monospace", color: evidencesPct > 60 ? ACCENT.olive : ACCENT.amber, lineHeight: 1, marginTop: '-0.25rem' }}>{evidencesPct}%</div>
          <div style={{ fontSize: '0.75rem', color: '#7A6A5A', marginTop: '0.25rem' }}>{validEvaluations.length} de {totalCriteria} criterios</div>
        </div>
      </div>

      {/* Due Diligence métricas */}
      <div style={CARD}>
        <h3 style={{ margin: '0 0 1rem', fontSize: '1rem', fontWeight: '600', color: '#2C2017' }}>
          Métricas Clave para Due Diligence ESG
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '0.875rem' }}>
          {[
            {
              icon: Factory, label: 'Intensidad de carbono',
              value: linked.sensors && ene1Intensity.m2 && (linked.sensors.scope1 + linked.sensors.scope2) > 0
                ? ((linked.sensors.scope1 + linked.sensors.scope2) / ene1Intensity.m2).toFixed(1)
                : ene1Intensity.co2 != null ? ene1Intensity.co2.toFixed(1) : '—',
              unit: 'kgCO₂eq/m²·año',
              benchmark: linked.sensors ? 'Sensores IoT · Scope 1+2 real' : (ene1Intensity.co2 != null ? 'ENE 1 diseño · < 40 nZEB' : 'Sin ENE 1 ni sensores'),
            },
            {
              icon: Lightning, label: 'Eficiencia energética',
              value: linked.sensors && ene1Intensity.m2 && linked.sensors.kwh > 0
                ? Math.round(linked.sensors.kwh / ene1Intensity.m2).toString()
                : ene1Intensity.kwh != null ? ene1Intensity.kwh.toFixed(1) : '—',
              unit: 'kWh/m²·año',
              benchmark: linked.sensors ? 'Sensores IoT · consumo total real' : (ene1Intensity.kwh != null ? 'ENE 1 diseño · < 80 kWh/m²' : 'Sin ENE 1 ni sensores'),
            },
            {
              icon: Drop, label: 'Consumo de agua',
              value: linked.sensors?.m3 > 0 && ene1Intensity.m2
                ? (linked.sensors.m3 * 1000 / ene1Intensity.m2).toFixed(1)
                : ag1Level != null ? `Nivel ${ag1Level.toFixed(1)}` : '—',
              unit: linked.sensors?.m3 > 0 ? 'litros/m²·año' : 'AG 1 (0-5)',
              benchmark: linked.sensors?.m3 > 0 ? 'Sensor caudal · < 30 l/m² recomendado' : (ag1Level != null ? 'AG 1 calculadora · ≥ 3 avanzado' : 'Sin AG 1 ni sensores'),
            },
            {
              icon: Leaf, label: 'Alineación Taxonomía UE',
              value: `${taxonomyAlignment}%`,
              unit: `${Math.round(taxonomyAlignment * 6 / 100)}/6 objetivos`,
              benchmark: '≥55% pts BREEAM/categoría = contribución sustancial',
            },
            {
              icon: Buildings, label: 'Estado del expediente',
              value: (() => {
                const audited = evaluations.some(e => e?.puntos_auditoria != null)
                if (audited) return 'Certificado'
                if (breeamPct >= 55) return 'En proceso'
                if (breeamPct >= 30) return 'Inicial'
                return 'Pendiente'
              })(),
              unit: 'BREEAM ES NC 2015',
              benchmark: `${breeamPct}% ponderado · ${totalPoints}/${maxPoints} pts`,
            },
            {
              icon: ShieldWarning, label: 'Riesgos regulatorios',
              value: regulatoryRisksCount.toString(),
              unit: `${dynamicRisks.filter(r => r.level === 'alto').length} riesgos altos totales`,
              benchmark: linked.iros?.length ? 'IROs abiertos + semáforo de riesgos' : 'Semáforo dinámico basado en BREEAM + ubicación',
            },
          ].map((m, i) => {
            const MIcon = m.icon
            return (
              <div key={i} style={{ background: '#FAF7F3', border: '1px solid #DDD5C8', borderRadius: '6px', padding: '0.875rem 1rem' }}>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '0.4rem' }}>
                  <MIcon size={16} style={{ color: '#7A6A5A', flexShrink: 0 }} />
                  <span style={{ fontSize: '0.775rem', fontWeight: '500', color: '#5C4A3A' }}>{m.label}</span>
                </div>
                <div style={{ fontSize: '1.35rem', fontWeight: '700', fontFamily: "'IBM Plex Mono', monospace", color: '#2C2017' }}>
                  {m.value}
                  <span style={{ fontSize: '0.7rem', color: '#7A6A5A', fontWeight: '400', marginLeft: '0.3rem' }}>{m.unit}</span>
                </div>
                <div style={{ fontSize: '0.7rem', color: '#B0A89E', marginTop: '0.2rem' }}>{m.benchmark}</div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Indicadores financieros ESG */}
      <div style={CARD}>
        <h3 style={{ margin: '0 0 1rem', fontSize: '1rem', fontWeight: '600', color: '#2C2017' }}>
          Indicadores Financieros ESG
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '0.875rem' }}>
          <div style={{ background: '#FAF7F3', border: '1px solid #DDD5C8', borderRadius: '6px', padding: '1rem' }}>
            <div style={{ fontSize: '0.7rem', fontWeight: '500', color: '#7A6A5A', marginBottom: '0.35rem' }}>Ahorro OPEX a 10 años</div>
            {opexSaving10y != null ? (
              <>
                <div style={{ fontSize: '1.5rem', fontWeight: '700', fontFamily: "'IBM Plex Mono', monospace", color: ACCENT.olive }}>
                  {(opexSaving10y / 1000).toFixed(0)}k€
                </div>
                <div style={{ fontSize: '0.7rem', color: '#B0A89E', marginTop: '0.2rem' }}>Snapshot Capex/OpEx · ahorro energía + agua real × 10</div>
              </>
            ) : (
              <>
                <div style={{ fontSize: '1.1rem', fontWeight: '700', fontFamily: "'IBM Plex Mono', monospace", color: '#B0A89E' }}>Pendiente</div>
                <div style={{ fontSize: '0.7rem', color: '#B0A89E', marginTop: '0.2rem', fontStyle: 'italic' }}>Cargar ahorro anual real en módulo Capex/OpEx</div>
              </>
            )}
          </div>
          <div style={{ background: '#FAF7F3', border: '1px solid #DDD5C8', borderRadius: '6px', padding: '1rem' }}>
            <div style={{ fontSize: '0.7rem', fontWeight: '500', color: '#7A6A5A', marginBottom: '0.35rem' }}>Elegibilidad financiación verde</div>
            <div style={{ display: 'flex', gap: '0.4rem', marginTop: '0.5rem', flexWrap: 'wrap' }}>
              {[
                { label: 'Green Bond', eligible: breeamPct >= 55 },
                { label: 'Green Loan', eligible: breeamPct >= 55 },
                { label: 'ICO Sos.', eligible: breeamPct >= 30 },
                { label: 'BEI', eligible: breeamPct >= 70 },
              ].map((inst, i) => (
                <span key={i} style={{
                  fontSize: '0.67rem', fontWeight: '500', padding: '0.2rem 0.5rem', borderRadius: '3px',
                  background: inst.eligible ? '#EDE7DE' : 'transparent',
                  border: inst.eligible ? 'none' : '1px solid #DDD5C8',
                  color: inst.eligible ? ACCENT.olive : '#B0A89E',
                }}>
                  {inst.label}
                </span>
              ))}
            </div>
          </div>
          <div style={{ background: '#FAF7F3', border: '1px solid #DDD5C8', borderRadius: '6px', padding: '1rem' }}>
            <div style={{ fontSize: '0.7rem', fontWeight: '500', color: '#7A6A5A', marginBottom: '0.35rem' }}>Riesgo climático físico</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginTop: '0.35rem' }}>
              <div style={{
                width: '8px', height: '8px', borderRadius: '50%',
                background: climateRisk.level === 'alto' ? ACCENT.coral
                  : climateRisk.level === 'medio' ? ACCENT.amber
                  : climateRisk.level === 'bajo' ? ACCENT.olive
                  : '#B0A89E',
              }} />
              <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#2C2017' }}>
                {climateRisk.level === 'no_evaluado' ? 'Sin evaluar' : climateRisk.level === 'alto' ? 'Alto' : climateRisk.level === 'medio' ? 'Medio' : 'Bajo'}
              </span>
            </div>
            <div style={{ fontSize: '0.7rem', color: '#B0A89E', marginTop: '0.2rem' }}>
              {climateRisk.activos.length > 0
                ? `${climateRisk.activos.map(a => `${a.tipo} sev ${a.sev}/5`).join(' · ')}`
                : climateRisk.arpsi === 'not_at_risk'
                  ? 'ARPSI MITECO verificado · sin riesgo inundación'
                  : 'Sin IROs físicos evaluados · verificar Monitorización'}
            </div>
          </div>
        </div>
      </div>

      {/* Semáforo de riesgos */}
      <div style={CARD}>
        <h3 style={{ margin: '0 0 1rem', fontSize: '1rem', fontWeight: '600', color: '#2C2017' }}>
          Semáforo de Riesgos
        </h3>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
            <thead>
              <tr style={{ background: '#EDE7DE' }}>
                {['Categoría de riesgo', 'Nivel', 'Medida de mitigación'].map(h => (
                  <th key={h} style={{ padding: '0.625rem 0.875rem', textAlign: 'left', fontWeight: '600', color: '#5C4A3A', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid #DDD5C8', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {dynamicRisks.map((r, i) => {
                const cfg = RISK_CONFIG[r.level]
                return (
                  <tr key={i} style={{ background: i % 2 === 0 ? '#FFFFFF' : '#FAF7F3', borderBottom: '1px solid #EDE7DE' }}>
                    <td style={{ padding: '0.75rem 0.875rem', fontWeight: '500', color: '#2C2017' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <Warning size={14} style={{ color: '#7A6A5A', flexShrink: 0 }} />
                        {r.category}
                      </div>
                    </td>
                    <td style={{ padding: '0.75rem 0.875rem' }}>
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
                        border: `1px solid ${cfg.color}`, color: cfg.color,
                        borderRadius: '3px', padding: '0.2rem 0.6rem',
                        fontSize: '0.7rem', fontWeight: '600',
                      }}>
                        <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: cfg.color }} />
                        {cfg.label}
                      </span>
                    </td>
                    <td style={{ padding: '0.75rem 0.875rem', color: '#7A6A5A', lineHeight: '1.5' }}>{r.mitigation}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Export */}
      <div style={CARD}>
        <h3 style={{ margin: '0 0 0.75rem', fontSize: '1rem', fontWeight: '600', color: '#2C2017' }}>
          Exportar Panel del Inversor
        </h3>
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
          <button onClick={handleExportPDF} style={{
            background: '#3B2F2F', color: '#F5F0EA', border: 'none', borderRadius: '4px',
            padding: '0.6rem 1.25rem', fontWeight: '600', cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem',
            fontFamily: "'DM Sans', sans-serif", transition: 'background 0.15s',
          }}
            onMouseOver={e => e.currentTarget.style.background = '#5C4A3A'}
            onMouseOut={e => e.currentTarget.style.background = '#3B2F2F'}
          >
            <DownloadSimple size={16} /> Exportar PDF
          </button>
          <button onClick={handleExportCSV} style={{
            background: 'transparent', color: '#5C4A3A', border: '1px solid #DDD5C8', borderRadius: '4px',
            padding: '0.6rem 1.25rem', fontWeight: '500', cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem',
            fontFamily: "'DM Sans', sans-serif", transition: 'background 0.15s',
          }}
            onMouseOver={e => e.currentTarget.style.background = '#EDE7DE'}
            onMouseOut={e => e.currentTarget.style.background = 'transparent'}
          >
            <FileCsv size={16} /> Exportar CSV
          </button>
        </div>
      </div>
    </div>
  )
}
